import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowUpRight, Eye } from "lucide-react";
import { CATEGORY_BY_SLUG, SHEET_URL } from "../data/products";

const ProductCard = ({ product, onQuickView, index = 0 }) => (
  <motion.article
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`product-card-${product.slug}`}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl hover:shadow-emerald-900/10"
  >
    <div className="relative h-52 overflow-hidden bg-night">
      <img
        src={product.image}
        alt={`${product.name} — ${product.tagline}`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-night/60 via-transparent to-transparent" />
      {product.status === "coming-soon" && (
        <span data-testid={`product-coming-soon-${product.slug}`} className="absolute left-4 top-4 rounded-full bg-amber-500 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-night">
          Coming soon
        </span>
      )}
      <button
        data-testid={`product-quickview-btn-${product.slug}`}
        onClick={() => onQuickView(product)}
        className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-night opacity-0 transition-all duration-300 hover:bg-teal-600 hover:text-white group-hover:opacity-100 max-lg:opacity-100"
      >
        <Eye className="h-3.5 w-3.5" /> Quick view
      </button>
    </div>

    <div className="flex flex-1 flex-col p-6">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-700">
        {CATEGORY_BY_SLUG[product.category]}
      </span>
      <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-night">{product.name}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{product.tagline}</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-mono text-[10px] font-medium text-emerald-800">
          {product.inclusion}
        </span>
        {product.species.slice(0, 2).map((s) => (
          <span key={s} className="rounded-full border border-slate-200 px-3 py-1 font-mono text-[10px] text-slate-500">
            {s}
          </span>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-4">
        <Link
          to={`/products/${product.slug}`}
          data-testid={`product-details-link-${product.slug}`}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-night transition-colors hover:text-teal-700"
        >
          Details <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
        <a
          href={SHEET_URL(product.slug)}
          data-testid={`product-sheet-link-${product.slug}`}
          className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors hover:text-teal-700"
        >
          <Download className="h-3.5 w-3.5" /> Sheet
        </a>
      </div>
    </div>
  </motion.article>
);

export default ProductCard;
